import { useContext, useEffect, useState } from "react"
import { useLazyQuery } from "@apollo/client"
import { Link } from "react-router-dom"
import _ from "lodash"
import { UserContext } from "../../context/userContext"
import AppBreadcrumb from "../../components/AppBreadcrumb/AppBreadcrumb"
import customStyles from "../../styles/customDataForms/CustomDataForms.module.css"
import CustomTable from "../../components/CustomTable/CustomTable"
import { DateTime } from "luxon"
// Loader
import { LoaderMedium } from "../../components/Loaders"

// Queries
import { GET_FULL_BLOCKS_ORGANIZATION } from "./queries"
import StandardPageHeader from "../../components/StandardPageHeader/StandardPageHeader"

export default function RecoveryPlan() {
  const userCtx = useContext(UserContext)
  const participant_id = userCtx.participant_id
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [organizationFilter, setOrganizationFilter] = useState(null)

  const [getData] = useLazyQuery(GET_FULL_BLOCKS_ORGANIZATION, {
    variables: {
      participant_id: participant_id,
      idTemplate: "recovery_plan_engagement",
      groupByIdStamp: true,
    },
    fetchPolicy: "no-cache",
    onCompleted: async (data) => {
      if (data.getAllRichBlocksByParticipantIdGroupedOrganization === null) {
        return setLoading(false)
      }
      const dataFormatted = JSON.parse(
        data.getAllRichBlocksByParticipantIdGroupedOrganization.data
      )

      const handleData = () =>
        _.map(dataFormatted, (template) => {
          // Process the data
          const blocks = _.groupBy(template, "block_name") // Group data by "block_name"
          const session_recovery_plan = _.isEmpty(blocks?.session__recovery_plan) ? {} : blocks?.session__recovery_plan[0]
          const session = _.isEmpty(blocks?.session) ? {} : blocks?.session[0]
          const staffID = session.staff_id
          const handleLocations = () => {
            const locationsList = _.map(
              session.values.session__location_ids,
              ({ label }) => label
            )
            return _.join(locationsList, ", ")
          }

          // Build the return object
          const newData = {
            date: new Date(session_recovery_plan.values.date_created),
            date_modified:
              session.values.date_changed !== "not-set"
                ? new Date(session.values.date_changed)
                : new Date(session.values.date_created),
            organization: session.organization_name,
            staff: _.unescape(session_recovery_plan.staff_full_name),
            location: handleLocations(),
            view: (
              <Link
                to={`/participant/recovery-plan/view/${session_recovery_plan.organization_id}/${session_recovery_plan.staff_id}/${session_recovery_plan.organization_name ? session_recovery_plan.organization_name : "Unknown Organization"}`}
                state={{
                  staffId: staffID,
                  previousTitle: "Recovery Plan",
                  previousRoute: "/participant/recovery-plan",
                }}
                className={customStyles.actionButton}
              >
                View
              </Link>
            ),
          }
          return newData
        })
      setData(_.compact(handleData()))
      const allOrganizations = _.map(
        handleData(),
        ({ organization }) => organization || "Unknown Organization"
      )
      setOrganizationFilter(_.uniq(allOrganizations).sort())
      setLoading(false)
    },
  })

  useEffect(() => {
    getData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formatDate = (value) => {
    if (!value) return ""
    return DateTime.fromJSDate(value).toLocal().toFormat("MM/dd/yyyy")
  }

  const filters = () => {
    return [
      {
        value: "organization",
        label: "Organization",
        options: organizationFilter,
        placeholder: "Select organization",
        showClear: true,
        type: "dropdown",
        default: "",
      }
    ]
  }

  const headers = () => {
    return [
      {
        value: "organization",
        label: "Organization",
        size: 2,
      },
      {
        value: "location",
        label: "Location",
        size: 2,
      },
      {
        value: "date",
        label: "Date Plan Created",
        size: 2,
      },
      {
        value: "date_modified",
        label: "Date Plan Updated",
        size: 2,
      },
      {
        value: "staff",
        label: "Updated By",
        size: 2,
      },
      {
        value: "view",
        label: "",
        action: true,
        size: 1,
      },
    ]
  }

  const mapOrganization = (organization) => {
    if (!organization) {
      return "Unknown Organization"
    }
    if (organization === false) {
      return "Unknown Organization"
    }
    return organization
  }

  const mapLocation = (location) => {
    if (!location) {
      return "Not set"
    }
    if (location === false) {
      return "Not set"
    }
    return location
  }

  const mapData = () => {
    return data?.map((d) => {
      return {
        organization: mapOrganization(d.organization),
        location: mapLocation(d.location),
        date: d.date ? formatDate(d.date) : "-",
        date_modified: d.date_modified ? formatDate(d.date_modified) : "-",
        staff: d.staff || "-",
        view: d.view || "-",
      }
    })
  }

  const sorting = () => {
    return [
      {
        value: "sortBy",
        label: "Sort by",
        options: [
          { name: "Organization", code: "organization" },
          { name: "Location", code: "location" },
          { name: "Date Plan Created", code: "date" },
          { name: "Date Plan Updated", code: "date_modified" },
          { name: "Updated By", code: "staff" },
        ],
        default: "organization",
        defaultOrder: true,
        placeholder: "Select option",
        showClear: true,
        type: "multiselect",
      },
    ]
  }

  return (
    <div>
      <div className="main-content">
        <AppBreadcrumb items={[{ label: "Recovery Plan" }]} />
        <div className="w-full pb-5">
          <StandardPageHeader
            title="Recovery Plan"
            refreshData={() => {
              setLoading(true)
              getData()
            }}
          />

          <hr className="line" />

          {loading ? <LoaderMedium /> :
            <CustomTable
              headers={headers()}
              collection={mapData()}
              filters={filters()}
              sorting={sorting()}
            />
          }
        </div>
      </div>
    </div>
  )
}
